import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { ref, computed, watch } from 'vue'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useRoute, useRouter } from 'vue-router'
import Tabs, { type TabItem } from '@/components/designSystem/Tabs/Tabs.vue'
import AuditoriumExceptionalities from './AuditoriumExceptionalities/index.vue'
import DsAttendance from './DsAttendance/Index.vue'
import HandoverApprovals from './HandoverApprovals/Index.vue'
import FacialBiometry from './FacialBiometry/Index.vue'
import CheckValidation from './CheckValidation/Index.vue'
import PriorityPayments from './PriorityPayments/Index.vue'
import { approvalsQueueAuthorizations, findApprovalsQueue, isApprovalsQueueKey } from './Hub/queues'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'


export default /*#__PURE__*/_defineComponent({
  __name: 'ApprovalsTabs',
  setup(__props) {

const headerStore = useHeaderStore()
const { flags } = useFeatureFlags()
const route = useRoute()
const router = useRouter()

const permissionsStore = usePermissionsStore()

const priorityPaymentsQueue = findApprovalsQueue('priorityPayments')

// Mesma regra da guarda de rota de cada fila: aba só do que o usuário consegue
// abrir (o perfil Extrator, por exemplo, só lê o Registro de Plantão).
const canAccessTab = (tab: TabItem) =>
  !isApprovalsQueueKey(tab.value) ||
  approvalsQueueAuthorizations(findApprovalsQueue(tab.value)).some((authorization) =>
    permissionsStore.checkAnyRessources(authorization)
  )

const allTabs = computed<TabItem[]>(() => {
  const tabs: TabItem[] = []
  if (flags.enabledHandoverV2) {
    tabs.push({ label: 'Trocas pelo aplicativo', value: 'handoverApprovals' })
  }
  tabs.push({ label: 'Pedidos de excepcionalidade', value: 'exceptionality' })
  if (flags.enabledDsAttendancePolicy) {
    tabs.push({
      label: 'Registro de plantão',
      value: 'attendance',
      testId: 'ds-attendance-tab-button'
    })
  }
  if (flags.enabledFacialBiometry) {
    tabs.push({
      label: 'Biometria facial',
      value: 'facialBiometry',
      testId: 'facial-biometry-tab-button'
    })
  }
  if (flags.enabledCheckValidation) {
    tabs.push({
      label: 'Avaliação de Evidências',
      value: 'checkValidation',
      testId: 'check-validation-tab-button'
    })
  }
  if (flags.enabledPriorityPayments) {
    tabs.push({
      label: priorityPaymentsQueue.pageTitle,
      value: 'priorityPayments',
      testId: 'priority-payments-tab-button'
    })
  }
  return tabs
})

const permittedTabs = computed(() => allTabs.value.filter(canAccessTab))
// Sem nenhuma aba permitida, mantém a lista das flags (a guarda da rota já barra
// quem não tem acesso a nada — nunca renderizar um Tabs vazio).
const computedTabList = computed<TabItem[]>(() =>
  permittedTabs.value.length > 0 ? permittedTabs.value : allTabs.value
)

const preferredDefaultTab = flags.enabledHandoverV2 ? 'handoverApprovals' : 'exceptionality'
const defaultTab = computedTabList.value.some((tab) => tab.value === preferredDefaultTab)
  ? preferredDefaultTab
  : (computedTabList.value[0]?.value ?? preferredDefaultTab)

// `?tab=` de uma aba desligada (link antigo, flag que caiu) abriria um slot
// vazio: cai na aba padrão.
const requestedTab = route.query.tab as string | undefined
const activeTab = ref(
  requestedTab && computedTabList.value.some((tab) => tab.value === requestedTab)
    ? requestedTab
    : defaultTab
)

const headerInfo: Record<string, { title: string; description: string }> = {
  handoverApprovals: {
    title: 'Trocas pelo aplicativo',
    description: 'Avalie e aprove solicitações de trocas e repasses de plantão'
  },
  exceptionality: {
    title: 'Pedidos de excepcionalidade',
    description:
      'Analise, aprove ou recuse solicitações excepcionais vinculadas a escalas e plantões'
  },
  attendance: {
    title: 'Registro de plantão',
    description: 'Visualize e gerencie os registros de plantão dos colaboradores'
  },
  facialBiometry: {
    title: 'Biometria facial',
    description: 'Gerencie a validação de biometria facial'
  },
  checkValidation: {
    title: 'Avaliação de Evidências',
    description: 'Decida sobre os registros de presença lançados manualmente no painel'
  },
  priorityPayments: {
    title: priorityPaymentsQueue.pageTitle,
    description: priorityPaymentsQueue.pageDescription
  }
}

const breadcrumb = { label: 'Aprovações', route: '/aprovacoes' }

watch(
  activeTab,
  (newTab) => {
    const info = headerInfo[newTab]
    if (info) {
      headerStore.setTitle(info.title, info.description, breadcrumb)
    }
  },
  { immediate: true }
)

watch(activeTab, (newTab) => {
  router.replace({ query: { tab: newTab } })
})

return (_ctx: any,_cache: any) => {
  return (
      _unref(flags).enabledDsAttendancePolicy ||
      _unref(flags).enabledHandoverV2 ||
      _unref(flags).enabledFacialBiometry ||
      _unref(flags).enabledCheckValidation ||
      _unref(flags).enabledPriorityPayments
    )
    ? (_openBlock(), _createBlock(Tabs, {
        key: 0,
        modelValue: activeTab.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeTab).value = $event)),
        tabs: computedTabList.value
      }, _createSlots({
        exceptionality: _withCtx(() => [
          _createVNode(AuditoriumExceptionalities)
        ]),
        _: 2
      }, [
        (_unref(flags).enabledHandoverV2)
          ? {
              name: "handoverApprovals",
              fn: _withCtx(() => [
                _createVNode(HandoverApprovals)
              ]),
              key: "0"
            }
          : undefined,
        (_unref(flags).enabledDsAttendancePolicy)
          ? {
              name: "attendance",
              fn: _withCtx(() => [
                _createVNode(DsAttendance)
              ]),
              key: "1"
            }
          : undefined,
        (_unref(flags).enabledFacialBiometry)
          ? {
              name: "facialBiometry",
              fn: _withCtx(() => [
                _createVNode(FacialBiometry)
              ]),
              key: "2"
            }
          : undefined,
        (_unref(flags).enabledCheckValidation)
          ? {
              name: "checkValidation",
              fn: _withCtx(() => [
                _createVNode(CheckValidation)
              ]),
              key: "3"
            }
          : undefined,
        (_unref(flags).enabledPriorityPayments)
          ? {
              name: "priorityPayments",
              fn: _withCtx(() => [
                _createVNode(PriorityPayments)
              ]),
              key: "4"
            }
          : undefined
      ]), 1032, ["modelValue", "tabs"]))
    : (_openBlock(), _createBlock(AuditoriumExceptionalities, { key: 1 }))
}
}

})